import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDeviceStore } from '../../shared/store/deviceStore';
import { useLanguageStore } from '../../shared/store/languageStore';
import { DeviceType, Language } from '../../shared/enum/EnumRepository';
import {
  getGuidePage,
  GUIDE_TAB_ORDER,
  type GuideTabId,
} from './guideContent';
import leftIcon from '../../asset/icon/left.png';

type GuideLocationState = {
  guideTab?: GuideTabId;
  /** 복귀 시 사용할 경로 (`pathname` + `search`). 없으면 히스토리 뒤로. */
  returnTo?: string;
  showGroupGuides?: boolean;
};

const getVisibleGuideTabOrder = (showGroupGuides: boolean): GuideTabId[] =>
  GUIDE_TAB_ORDER.filter((tab) => showGroupGuides || tab !== 'members');

const resolveGuideTabFromState = (
  state: unknown,
  visibleTabOrder: GuideTabId[],
): GuideTabId => {
  const tab = (state as GuideLocationState | null)?.guideTab;
  if (tab && visibleTabOrder.includes(tab)) return tab;
  return visibleTabOrder[0];
};

const Guide: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { deviceType } = useDeviceStore();
  const [language, setLanguage] = useState(() => useLanguageStore.getState().language);
  const showGroupGuides =
    (location.state as GuideLocationState | null)?.showGroupGuides === true;
  const visibleTabOrder = getVisibleGuideTabOrder(showGroupGuides);
  const [activeTab, setActiveTab] = useState<GuideTabId>(() =>
    resolveGuideTabFromState(
      location.state,
      getVisibleGuideTabOrder(
        (location.state as GuideLocationState | null)?.showGroupGuides === true,
      ),
    )
  );
  const page = getGuidePage(language, activeTab);
  const visibleSections = page.sections.filter(
    (section) => showGroupGuides || !section.groupOnly,
  );
  const containerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setActiveTab(
      resolveGuideTabFromState(
        location.state,
        getVisibleGuideTabOrder(showGroupGuides),
      ),
    );
  }, [location.key, showGroupGuides]);

  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  const handleBack = () => {
    const returnTo = (location.state as GuideLocationState | null)?.returnTo;
    if (returnTo && returnTo !== '/guide') {
      navigate(returnTo);
      return;
    }
    navigate('/');
  };

  const handleTabKeyDown = (e: React.KeyboardEvent) => {
    const order = visibleTabOrder;
    const currentIndex = order.indexOf(activeTab);
    if (currentIndex < 0) return;

    if (e.key === 'ArrowDown') {
      if (currentIndex < order.length - 1) {
        e.preventDefault();
        setActiveTab(order[currentIndex + 1]);
      }
    } else if (e.key === 'ArrowUp') {
      if (currentIndex > 0) {
        e.preventDefault();
        setActiveTab(order[currentIndex - 1]);
      }
    }
  };

  return (
    <Container
      ref={containerRef}
      tabIndex={0}
      onKeyDown={handleTabKeyDown}
      $deviceType={deviceType}
    >
      <GuideTopBar>
        <LanguageSelector>
          <LanguageOption
            $isActive={language === Language.KR}
            onClick={() => setLanguage(Language.KR)}
          >
            KOR
          </LanguageOption>
          <LanguageDivider>/</LanguageDivider>
          <LanguageOption
            $isActive={language === Language.EN}
            onClick={() => setLanguage(Language.EN)}
          >
            ENG
          </LanguageOption>
        </LanguageSelector>
        <TopBarSpacer aria-hidden />
        <BackButton
          type="button"
          onClick={handleBack}
          aria-label={language === Language.KR ? '이전 화면으로' : 'Go back'}
        >
          <BackIcon aria-hidden />
          {language === Language.KR ? '돌아가기' : 'Back'}
        </BackButton>
      </GuideTopBar>

      <PageTitle>
        {language === Language.KR ? '사용 가이드' : 'Usage Guide'}
      </PageTitle>
      <PageSubtitle>
        {language === Language.KR
          ? '각 페이지의 기본 사용법·주요 기능을 안내합니다.'
          : 'Basics and highlights for each page.'}
      </PageSubtitle>

      <BodyRow $deviceType={deviceType}>
        <TabSidebar
          role="tablist"
          aria-orientation="vertical"
          aria-label={language === Language.KR ? '페이지 선택' : 'Page tabs'}
          $deviceType={deviceType}
        >
          <TabPanel>
            {visibleTabOrder.map((id) => {
              const tab = getGuidePage(language, id);
              const isActive = activeTab === id;
              return (
                <TabButton
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  $isActive={isActive}
                  onClick={() => setActiveTab(id)}
                >
                  {tab.tabLabel}
                </TabButton>
              );
            })}
          </TabPanel>
        </TabSidebar>

        <ContentSection>
          <Intro>
            {page.intro.map((line, i) => (
              <React.Fragment key={i}>
                {i > 0 ? <br /> : null}
                {line}
              </React.Fragment>
            ))}
          </Intro>
          {page.introImages?.length ? (
            <IntroFigureRow>
              {page.introImages.map((src, i) => (
                <SectionFigure key={i}>
                  <SectionImage
                    src={src}
                    alt={
                      language === Language.KR
                        ? `${page.tabLabel} 소개 참고 이미지 ${i + 1}`
                        : `${page.tabLabel} intro reference ${i + 1}`
                    }
                    loading="lazy"
                    decoding="async"
                  />
                </SectionFigure>
              ))}
            </IntroFigureRow>
          ) : null}
          {visibleSections.map((section) => (
            <SectionBlock key={section.title}>
              <SmallTitle>{section.title}</SmallTitle>
              {section.imageSrc ? (
                <SectionFigure>
                  <SectionImage
                    src={section.imageSrc}
                    alt={
                      language === Language.KR
                        ? `${section.title} 참고 이미지`
                        : `${section.title} reference image`
                    }
                    loading="lazy"
                    decoding="async"
                  />
                </SectionFigure>
              ) : null}
              <BulletList $deviceType={deviceType}>
                {section.items.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </BulletList>
            </SectionBlock>
          ))}
        </ContentSection>
      </BodyRow>
    </Container>
  );
};

export default Guide;

const Container = styled.main<{ $deviceType: DeviceType }>`
  width: ${({ $deviceType }) =>
    $deviceType === DeviceType.MOBILE
      ? '100%'
      : $deviceType === DeviceType.TABLET
        ? 'min(800px, 100%)'
        : 'min(1000px, 85%)'};
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: ${({ $deviceType }) =>
    $deviceType === DeviceType.MOBILE ? '12vh 4vw 3vh 4vw' : '10vh 6vw 0 8vw'};
  overflow: hidden;
  outline: none;
`;

const BodyRow = styled.div<{ $deviceType: DeviceType }>`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: ${({ $deviceType }) =>
    $deviceType === DeviceType.MOBILE ? '8px' : 'clamp(16px, 2vw, 28px)'};
  flex: 1;
  min-height: 0;
`;

const TabSidebar = styled.nav<{ $deviceType: DeviceType }>`
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: ${({ $deviceType }) =>
    $deviceType === DeviceType.MOBILE
      ? 'clamp(60px, 16vw, 80px)'
      : 'clamp(70px, 8vw, 100px)'};
  padding-right: ${({ $deviceType }) =>
    $deviceType === DeviceType.MOBILE ? '4px' : '8px'};
  border-right: 1px solid ${({ theme }) => theme.colors.color_Gray_06};
`;

const TabPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  box-sizing: border-box;
`;

const GuideTopBar = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  flex-shrink: 0;
  gap: 12px;
`;

const TopBarSpacer = styled.div`
  flex: 1;
  min-width: 8px;
`;

const LanguageSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

/* LogoAndSetting `LogoIconBtn`과 동일 에셋·크기 (left.png) */
const BackIcon = styled.span`
  width: 8px;
  height: 8px;
  flex-shrink: 0;
  background-image: url(${leftIcon});
  background-size: cover;
  background-position: center;
  opacity: 0.7;
  transition: opacity 0.3s ease;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Footer_01};
  transition: color 0.2s ease;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    color: ${({ theme }) => theme.colors.color_Gray_03};

    ${BackIcon} {
      opacity: 1;
    }
  }

  &:focus,
  &:focus-visible {
    outline: none;
  }

  &::-moz-focus-inner {
    border: 0;
  }
`;

const LanguageOption = styled.button<{ $isActive: boolean }>`
  ${({ theme }) => theme.typography.Footer_01};
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.color_Gray_04 : theme.colors.color_Gray_05};
  background-color: transparent;
  border: none;
  cursor: pointer;
  transition: color 0.2s ease;
  -webkit-tap-highlight-color: transparent;

  &:focus,
  &:focus-visible {
    outline: none;
  }

  &::-moz-focus-inner {
    border: 0;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.color_Gray_04};
  }
`;

const LanguageDivider = styled.span`
  ${({ theme }) => theme.typography.Footer_01};
  color: ${({ theme }) => theme.colors.color_Gray_05};
  user-select: none;
`;

const PageTitle = styled.h1`
  margin: 8px 0 0;
  color: ${({ theme }) => theme.colors.color_Gray_01};
  ${({ theme }) => theme.typography.Body_02_1};
`;

const PageSubtitle = styled.p`
  margin: 0 0 8px;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
  line-height: 1.4;
`;

const TabButton = styled.button<{ $isActive: boolean }>`
  position: relative;
  width: 100%;
  margin: 0;
  padding: 8px 0;
  border: none;
  background: transparent;
  backdrop-filter: blur(4px);
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.color_Gray_01 : theme.colors.color_Gray_04};
  font-weight: ${({ theme, $isActive }) =>
    $isActive ? theme.fontWeight.medium : theme.fontWeight.regular};
  text-align: left;
  line-height: 1.35;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  text-decoration: ${({ $isActive }) => ($isActive ? 'underline' : 'none')};
  transition: color 0.2s ease;

  /* 클릭·탭 후 포커스 링 + 밑줄이 겹쳐 사각형처럼 보이는 현상 방지 */
  &:focus,
  &:focus-visible {
    outline: none;
  }

  &::-moz-focus-inner {
    border: 0;
  }

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 5%;
    right: 5%;
    bottom: 0;
    height: 1px;
    background: linear-gradient(
      to right,
      transparent,
      ${({ theme }) => theme.colors.color_Gray_06} 15%,
      ${({ theme }) => theme.colors.color_Gray_06} 95%,
      transparent
    );
  }

  &:hover {
    color: ${({ theme }) => theme.colors.color_Gray_02};
  }
`;

const ContentSection = styled.section`
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 100px;
`;

const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const IntroFigureRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 0 8px;
`;

const SectionFigure = styled.figure`
  margin: 0 0 4px;
`;

const SectionImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  border-radius: 2px;
  border: 1px solid ${({ theme }) => theme.colors.color_Gray_06};
`;

const Intro = styled.p`
  margin: 0 0 8px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
  line-height: 1.45;
`;

const SmallTitle = styled.h2`
  margin: 12px 0 4px;
  color: ${({ theme }) => theme.colors.color_Gray_01};
  ${({ theme }) => theme.typography.Body_03_1};
`;

const BulletList = styled.ul<{ $deviceType: DeviceType }>`
  width: ${({ $deviceType }) =>
    $deviceType === DeviceType.MOBILE
      ? '100%'
      : $deviceType === DeviceType.TABLET
        ? 'min(700px, 95%)'
        : 'min(820px, 100%)'};
  margin: 0;
  padding-left: 12px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
  line-height: 1.45;
  list-style: disc;

  li {
    margin-bottom: 6px;
  }
`;
